import { MATTERPORT_CONFIG, PROJECT_CONFIG } from '../config/projectConfig';
import { MatterportState, MatterportTag } from '../models/types';
import apiService from './apiService';
import { REACT_APP_MATTERPORT_SDK_KEY, TIMING, FALLBACK_SWEEP_IDS, VIEW_360_CONFIG } from '../const';
import bundleHotspotService from './bundleHotspotService';
import audioService from './audioService';
import videoZoneService from './videoZoneService';
import signageService from './signageService';
import navBillboardService from './navBillboardService';
import navDirectionSignService from './navDirectionSignService';
import locationMediaService from './locationMediaService';
import deviceProfile from '../utils/deviceProfile';

class MatterportService {
  constructor() {
    this.state = new MatterportState();
    this.sdkKey = REACT_APP_MATTERPORT_SDK_KEY;
    this.iframe = null;
    this.eventHandlers = new Map();
    this.autoNavigationCompleted = true; // disabled — set to false to re-enable auto-navigation on load
    this._lastPosition = null;
    this._hotspotPlaced = false;
    this._lastAudioCheck = 0;
    this.subscriptions = []; // Track all SDK subscriptions for cleanup
    this.handlersSetup = false; // Guard against duplicate event handler setup
    this._tourIndex = -1;       // Current manual-tour step index (-1 = inactive)
    this._tourActive = false;   // Whether the manual tour is running
    this._tourPaused = false;   // Whether the tour is paused (narration + advance halted)
    this._quickNavFlying = false; // True while a footer/floor quick-nav fly is in progress
    this._lastSweepArrivalAt = 0; // Timestamp (Date.now()) of the most recent Sweep.current change
  }

  // Initialize Matterport SDK (Bundle mode)
  async initialize(iframeElement, showcaseWindow) {
    // Prevent double initialization — if already connected or in-progress, bail out.
    if (this._initializingPromise) {
      console.log('⚠️ Initialization already in progress, returning existing promise');
      return this._initializingPromise;
    }
    if (this.state.isConnected && this.state.sdk) {
      console.log('⚠️ SDK already initialized, skipping duplicate initialize() call');
      return true;
    }

    this._initializingPromise = this._doInitialize(iframeElement, showcaseWindow);
    try {
      const result = await this._initializingPromise;
      return result;
    } finally {
      this._initializingPromise = null;
    }
  }

  async _doInitialize(iframeElement, showcaseWindow) {
    // Cleanup any existing subscriptions if re-initializing
    if (this.subscriptions.length > 0) {
      console.log('⚠️ Re-initializing - cleaning up existing subscriptions first');
      this.cleanupSubscriptions();
    }

    this.iframe = iframeElement;
    this.showcaseWindow = showcaseWindow;
    console.log('🚀 Starting Matterport Bundle SDK initialization...');
    console.log('📋 SDK Key available:', !!this.sdkKey);
    
    if (!this.sdkKey) {
      console.error('❌ No Matterport SDK key found. Please set REACT_APP_MATTERPORT_SDK_KEY in .env.local');
      this.state.isConnected = false;
      return false;
    }

    if (!showcaseWindow) {
      console.error('❌ showcaseWindow is not available yet — iframe may not have loaded');
      this.state.isConnected = false;
      return false;
    }
    
    try {
      // Bundle SDK: wait for MP_SDK to initialize inside the iframe before connecting
      console.log('⏳ Waiting for MP_SDK to become available on contentWindow...');
      const mpSDK = await this._waitForMPSDK(showcaseWindow);
      console.log('🔗 Connecting via Bundle SDK (contentWindow.MP_SDK)...');
      this.state.sdk = await mpSDK.connect(showcaseWindow);
      console.log('✅ Bundle SDK connected');

      // Expose sdk to window for console debugging
      window._mpSdk = this.state.sdk;

      // Expose matterportService for console access (logAllSweeps, etc.)
      window.matterportService = this;

      // Expose helper to get current camera rotation for config updates
      window.getCameraRotation = () => {
        const pose = this.state.currentPose;
        if (!pose || !pose.rotation) {
          console.log('❌ No camera pose available yet');
          return null;
        }
        const { x: pitch, y: yaw } = pose.rotation;
        const sweepId = this.state.currentSweep || 'unknown';
        console.log(`\n📍 Current Camera State:`);
        console.log(`   sweepId: "${sweepId}"`);
        console.log(`   pitch: ${pitch.toFixed(2)}`);
        console.log(`   yaw: ${yaw.toFixed(2)}`);
        console.log(`\n📋 Config snippet:`);
        console.log(`   "sweepId": "${sweepId}",`);
        console.log(`   "rotation": { "pitch": ${Math.round(pitch)}, "yaw": ${Math.round(yaw)} }`);
        return { sweepId, pitch: Math.round(pitch), yaw: Math.round(yaw) };
      };
      console.log('💡 Tip: Call getCameraRotation() in console to get current pitch/yaw for config');


      // Catch errors thrown inside the showcase iframe (cross-context errors)
      showcaseWindow.addEventListener('error', (event) => {
        console.error('🎬 Error inside showcase frame:', event.message, event.error);
      });
      showcaseWindow.addEventListener('unhandledrejection', (event) => {
        console.error('🎬 Unhandled rejection inside showcase frame:', event.reason);
      });

      this.state.isConnected = true;
      console.log('🎯 Bundle SDK connection successful');
      console.log('🧩 sdk.Scene available:', typeof this.state.sdk.Scene !== 'undefined');

      // Register hotspot component + load audio config BEFORE subscribing to Camera.pose.
      // Camera.pose fires early (during the first await in setupEventHandlers) so both must
      // be ready before any subscription callback runs, otherwise startAll() sees 0 zones.
      try {
        await bundleHotspotService.registerHotspotComponent(this.state.sdk);
        console.log('✅ Bundle hotspot component registered (hotspot will be placed after navigation)');
      } catch (hotspotError) {
        console.warn('⚠️ Could not register hotspot component:', hotspotError.message);
      }

      // Load location-based audio config from public/ at runtime (must be before Camera.pose subscription)
      await audioService.fetchAndLoadConfig('/config/locationAudioConfig.json');

      // Load location media config (photos / videos shown when user reaches a defined sweep)
      await locationMediaService.fetchAndLoadConfig('/config/locationMediaConfig.json');

      // Give audioService access to the showcase iframe window so that clicks/keys
      // inside the Matterport viewer can resume the AudioContext (same-origin propagation).
      // Then attempt to start the ambient track immediately — before Camera.pose fires —
      // so music begins as soon as the tour loads rather than on first user interaction.
      audioService.setIframeWindow(showcaseWindow);
      audioService.startAll().catch(err =>
        console.warn('⚠️ AudioService early startAll failed:', err?.message)
      );

      // Initialize video zones if the feature flag is on
      if (PROJECT_CONFIG.features.videosEnabled) {
        try {
          await videoZoneService.initialize(this.state.sdk);
          console.log('✅ VideoZoneService initialized');
        } catch (videoErr) {
          console.warn('⚠️ VideoZoneService initialization failed:', videoErr?.message);
        }
      }

      // Defer non-critical visual services (signage, billboards, direction signs)
      // to reduce the initial memory spike on mobile devices. These are purely
      // decorative and can safely load a few seconds after the core experience.
      const deferVisualServices = async () => {
        // Initialize ground signage system
        try {
          await signageService.initialize(this.state.sdk);
          console.log('✅ SignageService initialized (deferred)');
        } catch (signageErr) {
          console.warn('⚠️ SignageService initialization failed:', signageErr?.message);
        }

        // Wait between heavy initializers on weak devices to avoid GPU/RAM
        // spikes that crash iOS Safari Web Content process. Desktop runs
        // them back-to-back as before.
        if (deviceProfile.isWeakDevice) await new Promise(r => setTimeout(r, 1500));

        // Initialize editor-authored 3D billboard signage
        try {
          await navBillboardService.initialize(this.state.sdk);
          console.log('✅ NavBillboardService initialized (deferred)');
        } catch (billboardErr) {
          console.warn('⚠️ NavBillboardService initialization failed:', billboardErr?.message);
        }

        if (deviceProfile.isWeakDevice) await new Promise(r => setTimeout(r, 1500));

        // Initialize editor-authored 3D direction signposts
        try {
          await navDirectionSignService.initialize(this.state.sdk);
          console.log('✅ NavDirectionSignService initialized (deferred)');
        } catch (dirSignErr) {
          console.warn('⚠️ NavDirectionSignService initialization failed:', dirSignErr?.message);
        }
      };
      // Stagger so the GPU/memory settles after core init.
      // Desktop: 3s. Mid-tier mobile: 5s. Low-tier mobile: 8s.
      const visualDeferDelay =
        deviceProfile.tier === 'low' ? 8000 :
        deviceProfile.tier === 'mid' ? 5000 : 3000;
      setTimeout(deferVisualServices, visualDeferDelay);
      
      // Setup event handlers (subscribes to Camera.pose, Sweep.current, etc.)
      await this.setupEventHandlers();
      
      // Fetch and store all tags (don't fail if tags are not available)
      try {
        await this.fetchTags();
      } catch (tagError) {
        console.warn('⚠️ Could not fetch tags, but continuing initialization:', tagError.message);
      }

      // Suppress Matterport's built-in tag UI (discs + billboards)
      try {
        await this.suppressBuiltInTagUI();
      } catch (suppressError) {
        console.warn('⚠️ Could not suppress built-in tag UI:', suppressError.message);
      }

      // Load tour highlight translations from public/config at runtime
      try {
        const translationsResp = await fetch('/config/tourTranslations.json');
        window._tourTranslations = await translationsResp.json();
        console.log('✅ Tour translations loaded');
      } catch (translationErr) {
        console.warn('⚠️ Could not load tourTranslations.json:', translationErr?.message);
        window._tourTranslations = {};
      }

      // Store highlight reel snapshots and subscribe to Tour step changes
      try {
        const tourSnapshots = await this.state.sdk.Tour.getData();
        this._tourSnapshots = tourSnapshots;
        window._tourSnapshots = tourSnapshots;
        console.log(`🎬 Tour highlights loaded: ${tourSnapshots.length} steps`);
        
        // Log tour data in table format for debugging
        console.log('🎬 ===== TOUR SNAPSHOTS =====');
        console.table(tourSnapshots.map((snap, i) => ({
          '#': i + 1,
          sid: snap.sid || '(none)',
          name: snap.name || '(none)',
          mode: snap.mode || '(none)',
          position: snap.position ? `${snap.position.x?.toFixed(2)}, ${snap.position.y?.toFixed(2)}, ${snap.position.z?.toFixed(2)}` : 'N/A',
          rotation: snap.rotation ? `${snap.rotation.x?.toFixed(1)}°, ${snap.rotation.y?.toFixed(1)}°` : 'N/A',
        })));
        console.log('🎬 ============================');

        // Build snapshot SID → sweep SID mapping (they use different ID systems)
        await this._buildSnapshotSweepMap();

        // NOTE: We do NOT subscribe to Tour.currentStep or Tour.state because
        // we drive the tour manually via Sweep.moveTo() instead of sdk.Tour.start().
        // This avoids the SDK's own auto-advance timeline.
      } catch (tourErr) {
        console.warn('⚠️ Tour.getData() failed:', tourErr?.message);
      }

      // Send initial spatial context to backend (fire-and-forget; no-op
      // when chat is disabled — kept here for telemetry once chat returns).
      this.updateBackendSpatialContext();
      
      return true;
      
    } catch (error) {
      console.error('❌ Failed to initialize Matterport SDK:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      this.state.isConnected = false;
      return false;
    }
  }

  // Poll until MP_SDK is available on the showcase iframe's contentWindow.
  // The Bundle SDK initializes asynchronously after the iframe load event fires.
  _waitForMPSDK(showcaseWindow, maxAttempts = 40, intervalMs = 500) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        attempts++;
        if (showcaseWindow && showcaseWindow.MP_SDK) {
          console.log(`✅ MP_SDK available after ${attempts} attempt(s)`);
          resolve(showcaseWindow.MP_SDK);
        } else if (attempts >= maxAttempts) {
          reject(new Error(`MP_SDK not available on contentWindow after ${maxAttempts} attempts (${(maxAttempts * intervalMs) / 1000}s)`));
        } else {
          setTimeout(check, intervalMs);
        }
      };
      check();
    });
  }

  // Load Matterport script dynamically
  loadMatterportScript() {
    return new Promise((resolve, reject) => {
      console.log('📥 Loading Matterport script from:', MATTERPORT_CONFIG.sdk_script_url);
      
      const script = document.createElement('script');
      script.src = MATTERPORT_CONFIG.sdk_script_url;
      script.onload = () => {
        console.log('📦 Matterport script loaded successfully');
        resolve(window.MP_SDK);
      };
      script.onerror = (error) => {
        console.error('❌ Failed to load Matterport script:', error);
        reject(new Error('Failed to load Matterport script'));
      };
      document.head.appendChild(script);

      // Fallback timeout check
      let attempts = 0;
      const checkSDK = () => {
        attempts++;
        if (window.MP_SDK) {
          console.log('📦 Matterport SDK detected after', attempts, 'attempts');
          resolve(window.MP_SDK);
        } else if (attempts > TIMING.MATTERPORT.MAX_SCRIPT_CHECK_ATTEMPTS) {
          reject(new Error('SDK failed to load after maximum attempts'));
        } else {
          setTimeout(checkSDK, TIMING.MATTERPORT.SCRIPT_CHECK_INTERVAL);
        }
      };
      checkSDK();
    });
  }

  // Setup event handlers
  async setupEventHandlers() {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot setup event handlers: SDK not initialized');
      return;
    }

    // Guard: prevent duplicate subscriptions
    if (this.handlersSetup) {
      console.log('⚠️ Event handlers already set up, skipping duplicate setup');
      return;
    }

    try {
      console.log('🎯 Setting up Matterport event handlers...');

      // Listen for sweep changes (navigation)
      const sweepSub = this.state.sdk.Sweep.current.subscribe(async (sweepData) => {
        const debug = !!window.logSweepChange;
        if (debug) console.log('🔍 Sweep change:', sweepData.id);

        this.state.currentSweep = sweepData.id;
        this.state.spatialContext.current_sweep = sweepData.id;
        // Mark the moment the camera arrived at this sweep. POI auto-narration
        // uses this as a "user has settled at a sweep" signal so it doesn't
        // fire mid-fly while transitioning between points.
        this._lastSweepArrivalAt = Date.now();

        // Check if this is a 360 view
        this.state.is360View = this.is360View(sweepData.id);

        // Always try to find nearest tag on sweep change
        try {
          const currentPosition = sweepData.position;
          this.state.spatialContext.current_position = currentPosition;

          // Get all tags if we don't have them
          if (!this.state.tags.length) {
            await this.fetchTags();
          }

          // Find nearest tag (within 5 m) — silent unless debug.
          let nearestTag = null;
          let minDistance = Infinity;
          for (const tag of this.state.tags) {
            if (tag.position && typeof tag.position.x === 'number') {
              const dx = tag.position.x - currentPosition.x;
              const dy = tag.position.y - currentPosition.y;
              const dz = tag.position.z - currentPosition.z;
              const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
              if (distance < minDistance && distance < 5.0) {
                minDistance = distance;
                nearestTag = tag;
              }
            }
          }

          // Update current tag and spatial context
          if (nearestTag) {
            this.state.currentTag = nearestTag;
            this.state.spatialContext.current_tag = {
              id: nearestTag.id,
              label: nearestTag.label,
              description: nearestTag.description,
              position: nearestTag.position,
              sweep: nearestTag.sweep,
              distance: minDistance,
            };
          } else {
            this.state.currentTag = null;
            this.state.spatialContext.current_tag = null;
          }

          // Fire-and-forget: never block the sweep handler on a remote
          // backend round-trip (also a no-op when chat is disabled).
          this.updateBackendSpatialContext();
        } catch (error) {
          console.warn('⚠️ Error processing nearest tag:', error?.message);
        }

        // Fallback: if Camera.pose subscription misfired (e.g. returned null on first call)
        // and _hotspotPlaced was never set, trigger markers + audio from here instead.
        if (!this._hotspotPlaced && sweepData.position &&
            (sweepData.position.x !== 0 || sweepData.position.z !== 0)) {
          this._hotspotPlaced = true;
          console.log('🔄 Sweep-based fallback: placing audio markers + starting audio');
          audioService.getZonesWithMarkers().forEach(zone => {
            bundleHotspotService.placeHotspot(this.state.sdk, {
              position: zone.position,
              label: zone.label,
              color: Number(zone.markerColor ?? '0x00aaff'),
              radius: zone.markerRadius ?? 0.3,
            }).catch(err => console.warn(`⚠️ Marker for "${zone.label}" failed:`, err?.message));
          });
          audioService.startAll().catch(err => console.warn('⚠️ AudioService startAll failed:', err?.message));
        }

        // Update spatial audio listener on sweep change — throttled via shared _lastAudioCheck
        // to prevent rapid-fire gain jumps that cause clicks/pops.
        // On weak mobile devices we throttle harder (200ms vs 100ms) to
        // free CPU during the boot crash-window.
        if (sweepData.position) {
          const now = Date.now();
          const audioThrottle = deviceProfile.isWeakDevice ? 200 : 100;
          if (now - this._lastAudioCheck >= audioThrottle) {
            this._lastAudioCheck = now;
            audioService.updateListener(sweepData.position, null);
          }
        }

        this.notifyEventHandlers('sweepChanged', sweepData);
        console.log('✅ Sweep change processing complete');
      });
      this.subscriptions.push(sweepSub);

      // Cache sweep collection for path-walking (populated once data arrives)
      this._sweepCache = new Map(); // canonical id → ObservableSweepData
      const sweepDataSub = this.state.sdk.Sweep.data.subscribe({
        onAdded:   (id, sweep) => { this._sweepCache.set(id, sweep); },
        onUpdated: (id, sweep) => { this._sweepCache.set(id, sweep); },
        onRemoved: (id)        => { this._sweepCache.delete(id); },
      });
      this.subscriptions.push(sweepDataSub);
      const cameraSub = this.state.sdk.Camera.pose.subscribe((poseData) => {
        // Guard: Matterport fires this with poseData=null before the model loads.
        // Any property access on a null poseData throws inside the callback; the SDK
        // catches the error silently and the subscription is permanently dead.
        if (!poseData || !poseData.position) return;

        // Store latest camera pose for screen projection (custom tag overlay)
        this.state.currentPose = poseData;

        // Notify overlay so it updates pin positions only on real pose changes
        this.notifyEventHandlers('cameraPoseUpdated', poseData);

        // Log rotation for config updates (pitch/yaw in degrees) — only when enabled via window.logCameraRotation = true
        if (window.logCameraRotation && poseData.rotation) {
          const { x: pitch, y: yaw } = poseData.rotation;
          console.log(`🎥 pitch: ${pitch.toFixed(1)}°, yaw: ${yaw.toFixed(1)}°`);
        }

        // Verbose pose spam — opt-in only
        if (window.logCameraPose) {
          console.log('📸 Camera pose update — is360View:', this.state.is360View, 'pos:', poseData.position);
        }

        // On first real camera position: place audio POI markers + start spatial audio
        if (!this._hotspotPlaced &&
            (poseData.position.x !== 0 || poseData.position.z !== 0)) {
          this._hotspotPlaced = true;

          // Place a visible sphere marker for each audio zone with showMarker: true
          audioService.getZonesWithMarkers().forEach(zone => {
            bundleHotspotService.placeHotspot(this.state.sdk, {
              position: zone.position,
              label: zone.label,
              color: Number(zone.markerColor ?? '0x00aaff'),
              radius: zone.markerRadius ?? 0.3,
            }).catch(err => console.warn(`⚠️ Marker for "${zone.label}" failed:`, err?.message));
          });

          // Start all spatial audio sources (plays from their world positions)
          audioService.startAll().catch(err => console.warn('⚠️ AudioService startAll failed:', err?.message));
        }

        // Only update lastCameraPosition when not in a 360 view
        if (!this.state.is360View) {
          this.state.lastCameraPosition = poseData;
        } else {
          console.log('🎯 In 360 view - not updating camera position');
        }
        
        const currentPosition = poseData.position;
        this.state.spatialContext.current_position = currentPosition;

        // Throttle position-diff + backend update to max ~10fps (100ms)
        // to reduce CPU load on mobile (avoids per-frame Math.sqrt + network calls)
        const now = Date.now();
        if (now - (this._lastPosCheck || 0) >= 100) {
          this._lastPosCheck = now;

          // Only update backend if position changed significantly (> 0.1m)
          const lastPos = this._lastPosition || {};
          const dx = currentPosition.x - (lastPos.x || 0);
          const dy = currentPosition.y - (lastPos.y || 0);
          const dz = currentPosition.z - (lastPos.z || 0);
          const posDiffSq = dx * dx + dy * dy + dz * dz;
          
          if (posDiffSq > 0.01) { // 0.1m squared
            this._lastPosition = currentPosition;
            this.updateBackendSpatialContext();
          }
        }

        // Update spatial audio listener — throttled to 100ms (200ms on weak
        // mobile devices) for smooth spatial falloff without CPU spikes.
        const _audioThrottle = deviceProfile.isWeakDevice ? 200 : 100;
        if (now - this._lastAudioCheck >= _audioThrottle) {
          this._lastAudioCheck = now;
          audioService.updateListener(currentPosition, poseData.rotation);
          if (PROJECT_CONFIG.features.videosEnabled) {
            videoZoneService.updateListener(currentPosition);
          }
        }
      });
      this.subscriptions.push(cameraSub);

      // Track current zoom level so the Quick-Nav edit panel can capture it.
      // The SDK's ZoomData payload exposes the value as `level` (not `zoom`).
      try {
        const zoomSub = this.state.sdk.Camera?.zoom?.subscribe?.((z) => {
          const v = (z && typeof z.level === 'number') ? z.level
                  : (z && typeof z.zoom === 'number') ? z.zoom // legacy fallback
                  : null;
          this.state.currentZoom = v;
        });
        if (zoomSub) this.subscriptions.push(zoomSub);
      } catch (_) {}

      // Listen for tag events (clicks, opens, selections)
      try {
        console.log('🏷️ Setting up Tag event listeners...');

        // Use Tag.openTags to reliably detect when a user clicks or hovers a tag.
        // This fires regardless of allowAction settings and works in SDK 3.1 Bundle mode.
        if (this.state.sdk.Tag && this.state.sdk.Tag.openTags) {
          let lastHoveredId = null;

          const tagSub = this.state.sdk.Tag.openTags.subscribe((openTags) => {
            console.log('🏷️ openTags changed:', openTags);

            // --- Handle HOVER ---
            const hoveredId = openTags.hovered || null;
            if (hoveredId && hoveredId !== lastHoveredId) {
              // Close native billboard immediately
              this.state.sdk.Tag.close(hoveredId).catch(() => {});

              const storedTag = this.state.tags.find((t) => t.id === hoveredId);
              const tagData = {
                id: hoveredId,
                label: storedTag?.label || 'Unnamed',
                description: storedTag?.description || '',
                position: storedTag?.position || { x: 0, y: 0, z: 0 },
                sweep: storedTag?.sweep || ''
              };
              this.notifyEventHandlers('tagHovered', tagData);
            }
            if (!hoveredId && lastHoveredId) {
              this.notifyEventHandlers('tagHoverEnd', null);
            }
            lastHoveredId = hoveredId;

            // --- Handle CLICK/SELECT ---
            const selectedSet = openTags.selected;
            let openedId = null;

            if (selectedSet && typeof selectedSet.forEach === 'function') {
              selectedSet.forEach((id) => { openedId = id; });
            } else if (selectedSet && typeof selectedSet === 'string') {
              openedId = selectedSet;
            }

            if (openedId) {
              console.log('🏷️ Tag opened via openTags:', openedId);

              // Immediately close the built-in billboard so only our custom UI shows
              this.state.sdk.Tag.close(openedId).catch(() => {});

              // Find the full tag data and notify handlers
              const storedTag = this.state.tags.find((t) => t.id === openedId);
              const tagData = {
                id: openedId,
                label: storedTag?.label || 'Unnamed',
                description: storedTag?.description || '',
                position: storedTag?.position || { x: 0, y: 0, z: 0 },
                sweep: storedTag?.sweep || ''
              };

              this.state.currentTag = tagData;
              this.state.spatialContext.current_tag = tagData;
              this.updateBackendSpatialContext();
              this.notifyEventHandlers('tagSelected', tagData);
            }
          });
          this.subscriptions.push(tagSub);
          console.log('✅ Tag.openTags listener registered (hover + click)');
        } else {
          console.warn('⚠️ Tag.openTags not available');
        }
      } catch (tagEventError) {
        console.warn('⚠️ Could not set up tag event listeners:', tagEventError.message);
        console.log('📌 Available Tag methods:', this.state.sdk.Tag ? Object.keys(this.state.sdk.Tag) : 'Not available');
        console.log('📌 Available Mattertag methods:', this.state.sdk.Mattertag ? Object.keys(this.state.sdk.Mattertag) : 'Not available');
        console.log('📌 Available SDK methods:', this.state.sdk ? Object.keys(this.state.sdk) : 'Not available');
        console.log('📌 Available Camera methods:', this.state.sdk.Camera ? Object.keys(this.state.sdk.Camera) : 'Not available');
      }

      this.handlersSetup = true;
      console.log('🎯 Matterport event handlers setup complete');
    } catch (error) {
      console.error('❌ Error setting up event handlers:', error);
    }
  }

  // Suppress Matterport's built-in billboard popups AND disc markers entirely.
  // We no longer use allowAction — instead Tag.openTags.subscribe detects the open
  // and immediately calls Tag.close() so the built-in billboard is dismissed.
  // Docking and sharing are still disabled globally.
  // Native disc markers are hidden via Tag.editOpacity(id, 0) — only our custom UI shows.
  async suppressBuiltInTagUI() {
    if (!this.state.sdk) return;

    const sdk = this.state.sdk;

    try {
      // Disable dock and share buttons globally (these are per-space, not per-tag)
      if (sdk.Tag.toggleDocking) await sdk.Tag.toggleDocking(false).catch(() => {});
      if (sdk.Tag.toggleSharing) await sdk.Tag.toggleSharing(false).catch(() => {});
      console.log('✅ Docking and sharing disabled globally');
    } catch (err) {
      console.warn('⚠️ Could not disable docking/sharing:', err.message);
    }

    // Inject CSS into the showcase iframe to hide native tag billboards entirely.
    // This prevents the billboard from flashing before Tag.close() can dismiss it.
    try {
      const iframeDoc = this.showcaseWindow?.document;
      if (iframeDoc) {
        let css = `
          .tag-billboard,
          .annotation-preview,
          .annotations-preview-layer {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }

          /* Hide native Matterport tour panel + end-of-tour completion overlay */
          .tour-story,
          .tour-panel,
          .tour-story-container,
          .tour-story-bottom,
          .tour-bottom-bar,
          .tour-controls,
          .highlight-reel,
          .highlight-bar,
          .highlight-compacted,
          .minimalist-tour-controls,
          .highlight-tour-controls,
          .ctaContainer,
          [class*="tour-story"],
          [class*="tourStory"],
          [class*="tour-panel"],
          [class*="tourPanel"],
          [class*="tour-end"],
          [class*="tourEnd"],
          [class*="tour-complete"],
          [class*="tourComplete"],
          [class*="highlight-reel"] {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `;

        // Hide Matterport footer logo if configured
        if (!PROJECT_CONFIG.features.showMatterportLogo) {
          css += `
            .footer-ui .showcase-footer .footer-logo,
            .footer-ui .showcase-footer .logo-link {
              display: none !important;
              visibility: hidden !important;
            }
          `;
        }

        // Hide share button
        if (!PROJECT_CONFIG.features.showShareButton) {
          css += `
            .mp-nova-btn-icon .icon-share,
            .mp-nova-btn-icon .icon-share ~ *,
            .mp-nova-btn:has(.icon-share) {
              display: none !important;
            }
          `;
        }

        // Hide View in VR button
        if (!PROJECT_CONFIG.features.showVRButton) {
          css += `
            .view-in-vr {
              display: none !important;
            }
          `;
        }

        // Hide fullscreen button
        if (!PROJECT_CONFIG.features.showFullscreenButton) {
          css += `
            .fullscreen-mode {
              display: none !important;
            }
          `;
        }

        // Hide help & terms links in the footer
        if (!PROJECT_CONFIG.features.showFooterHelp) {
          css += `
            .showcase-footer-button-group {
              display: none !important;
            }
          `;
        }

        const style = iframeDoc.createElement('style');
        style.textContent = css;
        iframeDoc.head.appendChild(style);
        console.log('✅ Injected CSS to hide native tag billboards');
      }
    } catch (err) {
      console.warn('⚠️ Could not inject billboard-hiding CSS:', err.message);
    }

    // Nuke native Matterport tags entirely. Opacity 0 still leaves the
    // hover target + occasional billboard flash; `Tag.remove` (plural in
    // the current bundle) deletes them from the scene so the only POIs
    // rendered are the ones authored by this app's editor.
    const purgeAllTags = async () => {
      try {
        if (!sdk.Tag) return;
        let ids = [];
        if (typeof sdk.Tag.getData === 'function') {
          const raw = await sdk.Tag.getData().catch(() => []);
          ids = (Array.isArray(raw) ? raw : [])
            .map((t) => t.sid || t.id)
            .filter(Boolean);
        }
        if (!ids.length) return;
        // eslint-disable-next-line no-console
        console.log(`🧹 Purging ${ids.length} native Matterport tag(s)…`, ids);
        // Belt + suspenders:
        //   1. Force every disc to opacity 0 so even if `remove` is a
        //      no-op in this SDK build, the visual is gone.
        //   2. Call `Tag.remove(...ids)` to actually delete them from
        //      the scene graph. Some bundles accept `remove(ids[])`
        //      instead of rest args, so we try that as a fallback.
        if (typeof sdk.Tag.editOpacity === 'function') {
          await Promise.all(
            ids.map((id) => sdk.Tag.editOpacity(id, 0).catch(() => {}))
          );
        }
        if (typeof sdk.Tag.remove === 'function') {
          try {
            const removed = await sdk.Tag.remove(...ids);
            // eslint-disable-next-line no-console
            console.log('🧹 Tag.remove(...ids) returned:', removed);
          } catch (_) {
            try {
              const removed = await sdk.Tag.remove(ids);
              // eslint-disable-next-line no-console
              console.log('🧹 Tag.remove(ids[]) returned:', removed);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('🧹 Tag.remove failed; falling back to per-id.', e);
              for (const id of ids) {
                await sdk.Tag.remove(id).catch(() => {});
              }
            }
          }
        } else if (sdk.Mattertag && typeof sdk.Mattertag.remove === 'function') {
          for (const id of ids) {
            await sdk.Mattertag.remove(id).catch(() => {});
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('🧹 purgeAllTags error:', err);
      }
    };
    try {
      await purgeAllTags();
      // A couple of retries catch late-loaded tags (floor switch, etc.).
      setTimeout(() => { purgeAllTags(); }, 750);
      setTimeout(() => { purgeAllTags(); }, 2500);
      if (sdk.Tag?.data?.subscribe) {
        sdk.Tag.data.subscribe({
          onCollectionUpdated: () => { purgeAllTags(); },
          onAdded: () => { purgeAllTags(); },
          onUpdated: () => { purgeAllTags(); },
        });
      }
      console.log('✅ Native tag purge installed');
    } catch (err) {
      console.warn('⚠️ Could not purge native tags:', err.message);
    }
  }

  // ── Manual tour control (no SDK auto-play) ──────────────────────────────

  // Build a mapping from tour snapshot SIDs to actual sweep SIDs.
  // Tour.getData() returns snapshot IDs that differ from sweep IDs;
  // we match by position (x, z) to find the right sweep for Sweep.moveTo().
  async _buildSnapshotSweepMap() {
    this._snapToSweep = {};
    const snapshots = this._tourSnapshots;
    if (!snapshots?.length || !this.state.sdk) return;

    const sweeps = await new Promise((resolve) => {
      const list = [];
      const sub = this.state.sdk.Sweep.data.subscribe({
        onAdded: (_index, item) => {
          list.push({ sid: item.sid || _index, position: item.position });
        },
        onCollectionUpdated: () => { sub.cancel(); resolve(list); },
      });
      setTimeout(() => { sub.cancel(); resolve(list); }, 3000);
    });

    for (const snap of snapshots) {
      let bestDist = Infinity;
      let bestSid = null;
      for (const sw of sweeps) {
        const dx = sw.position.x - snap.position.x;
        const dz = sw.position.z - snap.position.z;
        const dist = dx * dx + dz * dz;
        if (dist < bestDist) { bestDist = dist; bestSid = sw.sid; }
      }
      this._snapToSweep[snap.sid] = bestSid;
    }
    console.log('🗺️ Snapshot→Sweep mapping built:', this._snapToSweep);
  }

  // Navigate the camera to a specific tour snapshot and emit the step event
  async _goToTourStep(index) {
    const snapshots = this._orderedSnapshots || this._tourSnapshots;
    if (!snapshots || index < 0 || index >= snapshots.length) return;
    const snapshot = snapshots[index];
    this._tourIndex = index;
    // New step = unpaused
    if (this._tourPaused) {
      this._tourPaused = false;
      this.notifyEventHandlers('tourResume', {});
    }
    // Cancel any running auto-pan from the previous step
    this._stopAutoPan();

    // Check if this step has images — if so, skip camera movement entirely.
    // The slideshow will cover the 3D view, so there’s no point flying the camera.
    const stepConfig = window._tourTranslations?.[snapshot.sid] ?? {};
    // Camera override layer: quickNavConfig.locations[*].camera takes precedence
    // over tourTranslations.json's per-sid camera block. Registered by
    // LocationQuickNav via setLocationCameraOverrides().
    const overrideCam = this._locationCameraOverrides?.[snapshot.sid];
    // Same layered merge as navigateToTourSnapshotBySid: prefer rotation vs pitch/yaw
    // based on which was supplied last.
    let mergedCam = null;
    const layers2 = [stepConfig.camera, overrideCam].filter(Boolean);
    if (layers2.length) {
      mergedCam = {};
      for (const layer of layers2) {
        if (layer.rotation && (layer.rotation.x != null || layer.rotation.y != null)) {
          delete mergedCam.pitch;
          delete mergedCam.yaw;
        }
        if (layer.pitch != null || layer.yaw != null) {
          delete mergedCam.rotation;
        }
        Object.assign(mergedCam, layer);
      }
    }
    const stepHasImages = (stepConfig.images ?? []).length > 0;
    // Synthetic snapshots (e.g. image-only stops without a Matterport scan)
    // carry no position/rotation — just emit the step event so overlays react,
    // and skip the camera move entirely.
    const isSynthetic = !!snapshot.synthetic;
    // Legacy hint: `camera.skipCamera: true` was used by the previous editor
    // to mean "don't touch the 3D camera". With the new editor an image
    // overlay stop also flies the camera to face the POI direction first,
    // so we only honour skipCamera when there's no captured pose to fly to.
    const hasPose = !!mergedCam?.sweepId;
    const skipCamera = mergedCam?.skipCamera === true && !hasPose;

    this.notifyEventHandlers('tourStep', { sid: snapshot.sid, index, snapshot });

    // Synthetic / scan-less stops without a captured pose: nothing to fly
    // to — just emit the step event and let TourOverlay handle narration.
    // When a synthetic stop DOES have a pose (e.g. nursery reusing the
    // entrance sweep), fall through and fly normally so the navigation
    // events fire and narration starts on time.
    if ((isSynthetic && !hasPose) || skipCamera) return;
    // tourTranslations.images path is legacy — preserve the old behaviour
    // when no captured pose exists. With a pose, fly anyway.
    if (stepHasImages && !hasPose) return;

    try {
      // Per-step camera config: override (quickNav) merged over tourTranslations
      const cam = mergedCam ?? {};
      const pitch = cam.pitch ?? cam.rotation?.x ?? snapshot.rotation?.x ?? 0;
      const yaw = cam.yaw ?? cam.rotation?.y ?? snapshot.rotation?.y ?? 0;
      // Use config override, then mapped sweep SID, then snapshot SID as fallback
      const sweepSid = cam.sweepId || this._snapToSweep?.[snapshot.sid] || snapshot.sid;

      // Default Matterport FLY transition between stops — single jump,
      // no sweep-by-sweep walking. POIs are hidden via NavigationHints
      // listening on navTransitionStart, and the black overlay is
      // suppressed via detail.cover === false so the user sees the fly.
      window.dispatchEvent(new CustomEvent('navTransitionStart', { detail: { cover: false } }));
      // Fly the camera to the next stop. We deliberately do NOT animate
      // zoom between guided-tour stops — per-stop `camera.zoom` would cause
      // a visible zoom-in/out during the fly when adjacent stops use
      // different zoom levels. The camera just moves; framing stays as it
      // was at the previous stop.
      try {
        await this.state.sdk.Sweep.moveTo(sweepSid, {
          rotation: { x: pitch, y: yaw },
          transition: this.state.sdk.Sweep.Transition.FLY,
          transitionTime: cam.flyTime ?? 1400,
        }).catch(() => {});
      } catch (_) {}
      window.dispatchEvent(new CustomEvent('navTransitionEnd'));
      // New simplified pan model: when `cam.pan === true` do a left→centre
      // →right cinematic sweep. The arc size is `cam.panAngle` if set,
      // else `cam.panDegrees` (legacy), else 15°. When `cam.pan === false`
      // skip pan entirely. When `pan` is undefined, fall back to the
      // legacy panDegrees/panSpeed behaviour for older configs.
      if (cam.pan === true) {
        const angle = (typeof cam.panAngle === 'number' && cam.panAngle > 0) ? cam.panAngle
                    : (typeof cam.panDegrees === 'number' && cam.panDegrees > 0) ? cam.panDegrees
                    : 15;
        this._startAutoPan(angle, cam.panSpeed ?? 1);
      } else if (cam.pan === false) {
        // explicit off — no pan
      } else {
        this._startAutoPan(cam.panDegrees, cam.panSpeed);
      }
    } catch (err) {
      console.warn('⚠️ Tour step navigation failed:', err?.message || err);
    }
  }

  // Navigate the camera to a tour snapshot by SID (used by the footer quick-nav).
  // Uses the same snap→sweep map and per-step camera config as the guided tour,
  // so manual jumps look consistent with tour transitions.
  //
  // `cameraOverride` (optional): per-call camera config, takes precedence over
  // any registered override (setLocationCameraOverrides) and tourTranslations.
  // Fields: { sweepId, rotation:{x,y} | pitch, yaw, zoom, flyTime, panDegrees, panSpeed }
  async navigateToTourSnapshotBySid(sid, cameraOverride = null) {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot navigate: SDK not initialized');
      return false;
    }
    const snap = (this._tourSnapshots || []).find(s => s.sid === sid);
    const registered = this._locationCameraOverrides?.[sid];
    const translations = window._tourTranslations?.[sid]?.camera;
    // Merge layers. When a later layer supplies `rotation: {x,y}`, strip the
    // earlier layer's `pitch`/`yaw` so the rotation actually takes effect
    // (otherwise pitch/yaw still win via the fallback chain below).
    // Order: translations < per-call cameraOverride (footer passes the
    // on-disk camera here) < registered (live editor overrides). The
    // editor MUST win or live edits to pan/zoom/angle don't take effect
    // until the user saves and reloads.
    const layers = [translations, cameraOverride, registered].filter(Boolean);
    const cam = {};
    for (const layer of layers) {
      if (layer.rotation && (layer.rotation.x != null || layer.rotation.y != null)) {
        delete cam.pitch;
        delete cam.yaw;
      }
      if (layer.pitch != null || layer.yaw != null) {
        delete cam.rotation;
      }
      Object.assign(cam, layer);
    }
    // eslint-disable-next-line no-console
    console.log(`🎥 navigateTo ${sid}`, { cameraOverride, registered, translations, merged: cam });

    // If there's no Matterport snapshot and no sweepId override, we can't move.
    if (!snap && !cam.sweepId) {
      console.warn(`⚠️ Tour snapshot not found and no sweepId override: ${sid}`);
      return false;
    }

    const pitch = cam.pitch ?? cam.rotation?.x ?? snap?.rotation?.x ?? 0;
    const yaw = cam.yaw ?? cam.rotation?.y ?? snap?.rotation?.y ?? 0;
    const sweepSid = cam.sweepId || this._snapToSweep?.[sid] || sid;
    // eslint-disable-next-line no-console
    console.log(`🎥 → sweep=${sweepSid} pitch=${pitch} yaw=${yaw} zoom=${cam.zoom ?? 'default'}`);
    try {
      // Quick-nav now uses the same default-Matterport FLY transition as the
      // guided tour — no black fade. The `cover: false` detail tells the
      // black-overlay listener in MatterportViewer to stay hidden.
      const wasOnSameSweep = this.state.currentSweep === sweepSid;
      this._quickNavFlying = true;
      window.dispatchEvent(new CustomEvent('navTransitionStart', { detail: { cover: false } }));
      try {
        // Run zoom in parallel with the FLY using ease-in-out so the zoom
        // change is concentrated mid-flight (masked by the SDK's camera
        // arc) rather than at the start (perceived as a zoom-out at the
        // previous stop) or at the end (perceived as a jump on arrival).
        const flyTime = cam.flyTime ?? 1400;
        await Promise.all([
          this.state.sdk.Sweep.moveTo(sweepSid, {
            rotation: { x: pitch, y: yaw },
            transition: this.state.sdk.Sweep.Transition.FLY,
            transitionTime: flyTime,
          }).catch(() => {}),
          (cam.zoom != null)
            ? this._smoothZoomTo(cam.zoom, flyTime, 'easeInOut').catch(() => {})
            : Promise.resolve(),
        ]);
      } catch (_) {}
      // When the user clicks the same stop's button again, Sweep.moveTo is a
      // no-op and the rotation argument is ignored. Snap rotation explicitly
      // in that case so the camera still ends up at the captured yaw.
      if (wasOnSameSweep) {
        this.state.sdk.Camera.setRotation?.({ x: pitch, y: yaw }, { speed: 100 }).catch(() => {});
      }
      window.dispatchEvent(new CustomEvent('navTransitionEnd'));
      // Same pan model as guided-tour steps (see _goToTourStep).
      if (cam.pan === true) {
        const angle = (typeof cam.panAngle === 'number' && cam.panAngle > 0) ? cam.panAngle
                    : (typeof cam.panDegrees === 'number' && cam.panDegrees > 0) ? cam.panDegrees
                    : 15;
        this._startAutoPan(angle, cam.panSpeed ?? 1);
      } else if (cam.pan === false) {
        // explicit off
      } else if (cam.panDegrees) {
        this._startAutoPan(cam.panDegrees, cam.panSpeed);
      }
      this._quickNavFlying = false;
      return true;
    } catch (err) {
      console.warn(`⚠️ Nav to snapshot ${sid} failed:`, err?.message || err);
      this._quickNavFlying = false;
      return false;
    }
  }

  /**
   * Register a map of tourSid → camera override, sourced from quickNavConfig.
   * Overrides apply to both manual quick-nav (navigateToTourSnapshotBySid) and
   * guided-tour steps (_goToTourStep). Each entry is an object shaped like:
   *   { sweepId, rotation:{x,y} | pitch, yaw, zoom, flyTime, panDegrees, panSpeed }
   * Pass an empty object to clear.
   */
  setLocationCameraOverrides(map) {
    this._locationCameraOverrides = (map && typeof map === 'object') ? { ...map } : {};
  }

  // Slow cinematic horizontal camera pan (mimics Matterport tour behaviour).
  // Uses a single Camera.rotate() call with a slow speed so the SDK animates
  // Cinematic right-only pan: from the captured default view, rotate the
  // camera right by `degrees` at `speed`. Single direction, single shot.
  // NOTE: Camera.rotate uses positive yaw = right (opposite to
  // Camera.setRotation, which uses positive yaw = left).
  async _startAutoPan(degrees = 15, speed = 1) {
    this._stopAutoPan();
    if (!degrees) return;
    this._panActive = true;
    window.dispatchEvent(new CustomEvent('autoPanStart'));
    try {
      await this.state.sdk.Camera.rotate(degrees, 0, { speed }).catch(() => {});
    } finally {
      this._panActive = false;
      window.dispatchEvent(new CustomEvent('autoPanEnd'));
    }
  }

  _stopAutoPan() {
    if (this._panActive) {
      this._panActive = false;
      this.state.sdk?.Camera?.stop?.().catch(() => {});
      window.dispatchEvent(new CustomEvent('autoPanEnd'));
    }
  }

  // Smoothly ease the SDK zoom from the current value to `target` over
  // `durationMs`. The native Camera.zoomTo() jumps instantly which feels
  // jarring next to the FLY transition — this helper uses small ticks
  // with cubic ease-out so the zoom matches the fly's tempo.
  async _smoothZoomTo(target, durationMs = 1400, easing = 'easeOut') {
    const cam = this.state.sdk?.Camera;
    if (!cam || typeof cam.zoomTo !== 'function') return;
    const from = (typeof this.state.currentZoom === 'number') ? this.state.currentZoom : 1;
    if (Math.abs(from - target) < 0.01) {
      return cam.zoomTo(target).catch(() => {});
    }
    const start = performance.now();
    const ease = easing === 'easeInOut'
      ? (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2) // cubic in-out
      : (t) => 1 - Math.pow(1 - t, 3); // cubic out
    return new Promise((resolve) => {
      const step = () => {
        const t = Math.min(1, (performance.now() - start) / durationMs);
        const v = from + (target - from) * ease(t);
        cam.zoomTo(v).catch(() => {});
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(step);
    });
  }

  // Start the guided tour manually (no SDK auto-advance)
  async startTour() {
    const list = this._orderedSnapshots || this._tourSnapshots;
    if (!list?.length) {
      console.warn('⚠️ No tour snapshots available');
      return;
    }
    this._tourActive = true;
    await this._goToTourStep(0);
    console.log('▶️ Tour started (manual mode)');
  }

  // Configure the guided-tour stop order. `sids` is an ordered array of
  // snapshot SIDs (matching `quickNavConfig.locations[*].tourSid`). Snapshots
  // not present in the SDK's tour list are kept as synthetic placeholders
  // (used by image-only stops like Nursery that have no Matterport scan).
  // This filters out any extra SDK tour stops so Next/Prev only walks the
  // curated set in the configured order — no flashes through unwanted points.
  setTourOrder(sids) {
    if (!Array.isArray(sids) || sids.length === 0) {
      this._orderedSnapshots = null;
      return;
    }
    const bySid = new Map((this._tourSnapshots || []).map((s) => [s.sid, s]));
    this._orderedSnapshots = sids.map((sid) => bySid.get(sid) || { sid, synthetic: true });
    console.log(`🎬 Tour order configured: ${this._orderedSnapshots.length} stops`);
  }

  // Stop the guided tour and return to the entry gate (first tour snapshot)
  // Pass { skipReturn: true } to stop without navigating back (e.g. when quick nav takes over)
  async stopTour({ skipReturn = false } = {}) {
    if (!this._tourActive) return;
    this._tourActive = false;
    this._tourPaused = false;
    this._tourIndex = -1;
    this._stopAutoPan();
    this.notifyEventHandlers('tourStop', {});
    if (skipReturn) return;
    // Fly back to the entry gate — use the first tour snapshot's position
    const firstSnap = this._tourSnapshots?.[0];
    const sweepSid = firstSnap && this._snapToSweep?.[firstSnap.sid];
    if (sweepSid) {
      try {
        await this.state.sdk.Sweep.moveTo(sweepSid, {
          rotation: { x: firstSnap.rotation.x, y: firstSnap.rotation.y },
          transition: this.state.sdk.Sweep.Transition.FLY,
          transitionTime: 1500,
        });
        return;
      } catch (_) {}
    }
    await this.resetToStart().catch(() => {});
  }

  // Navigate to the previous tour step
  async tourPrev() {
    if (!this._tourActive || this._tourIndex <= 0) return;
    await this._goToTourStep(this._tourIndex - 1);
  }

  // Navigate to the next tour step (or stop if this was the last one)
  async tourNext() {
    const list = this._orderedSnapshots || this._tourSnapshots;
    if (!this._tourActive || !list) return;
    const nextIndex = this._tourIndex + 1;
    if (nextIndex >= list.length) {
      // Tour finished naturally — emit a dedicated event so the UI can
      // schedule its post-tour idle behaviour (auto-reset to start).
      this.notifyEventHandlers('tourComplete', {});
      await this.stopTour({ skipReturn: true });
      return;
    }
    await this._goToTourStep(nextIndex);
  }

  // Pause the tour in place — halts narration and the auto-pan; no step change
  pauseTour() {
    if (!this._tourActive || this._tourPaused) return;
    this._tourPaused = true;
    this._stopAutoPan();
    audioService.pauseTourNarration();
    this.notifyEventHandlers('tourPause', {});
  }

  // Resume a paused tour from where it left off
  resumeTour() {
    if (!this._tourActive || !this._tourPaused) return;
    this._tourPaused = false;
    audioService.resumeTourNarration();
    this.notifyEventHandlers('tourResume', {});
  }

  isTourPaused() {
    return this._tourPaused;
  }

  // Whether the manual guided tour is currently running (between startTour
  // and stopTour). Used to suppress POI auto-narration during the tour.
  isTourActive() {
    return this._tourActive === true;
  }

  // Whether a footer / floor quick-nav fly is currently in progress.
  // Used to suppress POI auto-narration while the camera is transitioning.
  isQuickNavFlying() {
    return this._quickNavFlying === true;
  }

  // Milliseconds since the camera last arrived at a sweep (Sweep.current
  // change). NavigationHints uses this to require the user has settled at
  // a sweep before triggering POI auto-narration.
  msSinceLastSweepArrival() {
    if (!this._lastSweepArrivalAt) return Infinity;
    return Date.now() - this._lastSweepArrivalAt;
  }

  // Get screen-projected positions for all tags (used by CustomTagOverlay)
  getTagScreenPositions(cameraPose) {
    if (!this.state.sdk || !cameraPose || !this.iframe) return [];

    const sdk = this.state.sdk;
    const windowSize = {
      w: this.iframe.clientWidth,
      h: this.iframe.clientHeight
    };

    const cameraPos = cameraPose.position;
    const results = [];

    for (const tag of this.state.tags) {
      if (!tag.position || typeof tag.position.x !== 'number') continue;

      try {
        const screen = sdk.Conversion.worldToScreen(
          tag.position,
          cameraPose,
          windowSize
        );

        // z > 0 means the point is in front of the camera
        const inFront = screen.z > 0;
        const inBounds = screen.x >= 0 && screen.x <= windowSize.w &&
                         screen.y >= 0 && screen.y <= windowSize.h;

        // Calculate distance from camera for scaling
        const dx = tag.position.x - cameraPos.x;
        const dy = tag.position.y - cameraPos.y;
        const dz = tag.position.z - cameraPos.z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        // Backface culling: if stemVector exists, the dot product with (camPos - tagPos)
        // tells us whether the tag's surface faces toward the camera.
        // dot > 0 → stem points toward camera → visible side
        // dot ≤ 0 → camera is behind the wall the tag is on → occluded
        let facingCamera = true;
        if (tag.stemVector &&
            typeof tag.stemVector.x === 'number' &&
            (tag.stemVector.x !== 0 || tag.stemVector.y !== 0 || tag.stemVector.z !== 0)) {
          // Vector from tag to camera
          const tcx = cameraPos.x - tag.position.x;
          const tcy = cameraPos.y - tag.position.y;
          const tcz = cameraPos.z - tag.position.z;
          const dot = tag.stemVector.x * tcx + tag.stemVector.y * tcy + tag.stemVector.z * tcz;
          facingCamera = dot > 0;
        }

        results.push({
          id: tag.id,
          label: tag.label,
          description: tag.description,
          screenX: screen.x,
          screenY: screen.y,
          visible: inFront && inBounds && facingCamera,
          distance
        });
      } catch (err) {
        // worldToScreen can fail for invalid positions — skip silently
      }
    }

    return results;
  }

  // Get the current camera pose (stored from Camera.pose subscription)
  getCurrentPose() {
    return this.state.currentPose || null;
  }

  // Get the current sweep id (the user's current Matterport panorama).
  getCurrentSweepId() {
    return this.state.currentSweep || null;
  }

  // Get the current camera zoom level (1 = default field of view).
  // Returns null until the first Camera.zoom event fires.
  getCurrentZoom() {
    return (typeof this.state.currentZoom === 'number') ? this.state.currentZoom : null;
  }

  /**
   * Project a world-space 3D point to 2D screen coordinates using the current
   * (or supplied) camera pose. Returns { x, y, z, inFront } or null if the SDK
   * isn't ready. Reuses the same projection path as getTagScreenPositions.
   */
  worldToScreen(worldPos, cameraPose) {
    const pose = cameraPose || this.state.currentPose;
    if (!this.state.sdk || !pose || !this.iframe || !worldPos) return null;
    try {
      const windowSize = {
        w: this.iframe.clientWidth,
        h: this.iframe.clientHeight,
      };
      const screen = this.state.sdk.Conversion.worldToScreen(worldPos, pose, windowSize);
      return {
        x: screen.x,
        y: screen.y,
        z: screen.z,
        inFront: screen.z > 0,
        windowSize,
      };
    } catch (_) {
      return null;
    }
  }

  /**
   * Returns the neighboring sweeps of the current sweep as
   * [{ id, position: {x,y,z} }, ...]. Empty array if the sweep cache
   * has not populated yet or the user is in a 360 view.
   */
  getCurrentSweepNeighbors() {
    const currentId = this.state.currentSweep;
    if (!currentId || !this._sweepCache) return [];
    const current = this._sweepCache.get(currentId);
    if (!current || !Array.isArray(current.neighbors)) return [];
    const out = [];
    for (const nid of current.neighbors) {
      const n = this._sweepCache.get(nid);
      if (n?.position && typeof n.position.x === 'number') {
        out.push({
          id: n.id,
          position: { x: n.position.x, y: n.position.y, z: n.position.z },
        });
      }
    }
    return out;
  }

  // Fetch all tags from Matterport space
  async fetchTags() {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot fetch tags: SDK not initialized');
      return [];
    }

    try {
      console.log('📌 Fetching Matterport tags...');
      
      let tags = [];
      
      // Try different tag APIs available in this SDK version
      if (this.state.sdk.Tag && typeof this.state.sdk.Tag.getData === 'function') {
        console.log('📌 Using Tag.getData() API...');
        tags = await this.state.sdk.Tag.getData();
      } else if (this.state.sdk.Mattertag && typeof this.state.sdk.Mattertag.getData === 'function') {
        console.log('📌 Using Mattertag.getData() API...');
        tags = await this.state.sdk.Mattertag.getData();
      }
      
      console.log('📌 Raw tags data:', tags);
      
      // Process tags with proper position data
      this.state.tags = tags.map(tag => {
        // Get the correct position from either anchorPosition or position
        const position = tag.anchorPosition || tag.position || null;
        console.log(`📌 Processing tag: ${tag.label}, Position:`, position);
        
        return new MatterportTag(
          tag.sid || tag.id || Math.random().toString(),
          tag.label || 'Unnamed',
          tag.description || '',
          position, // Use the correct position
          tag.sweepId || tag.sweep || '',
          tag.stemVector || null // surface normal for occlusion culling
        );
      });
      
      console.log('📌 Processed tags:', this.state.tags.map(t => ({
        label: t.label,
        position: t.position
      })));

      // Log all tags with full details on startup
      console.log(`🏷️ ===== ALL TAGS (${this.state.tags.length}) =====`);
      console.table(this.state.tags.map((t, i) => ({
        '#': i + 1,
        id: t.id,
        label: t.label,
        description: t.description || '(none)',
        sweep: t.sweep || '(none)',
        x: t.position?.x?.toFixed(2) ?? 'N/A',
        y: t.position?.y?.toFixed(2) ?? 'N/A',
        z: t.position?.z?.toFixed(2) ?? 'N/A',
      })));
      console.log('🏷️ ================================');

      await this.sendTagsToBackend();
      return this.state.tags;
    } catch (error) {
      console.error('❌ Error fetching tags:', error);
      return [];
    }
  }

  // Send tags to backend
  async sendTagsToBackend() {
    // No consumer when chat is disabled — skip the HTTP round-trip so
    // initialization isn't gated on backend availability.
    if (!PROJECT_CONFIG.features.chatEnabled) return;
    try {
      console.log('📤 Sending tags to backend...');
      await apiService.updateMatterportTags(this.state.tags);
      console.log('📤 Tags sent to backend successfully');
    } catch (error) {
      console.warn('⚠️ Backend tags endpoint not available yet:', error.message);
      // Don't throw error - this is expected until backend is updated
    }
  }

  // Update backend with current spatial context
  async updateBackendSpatialContext() {
    // Short-circuit when the AI chat feature is disabled — there is no
    // consumer of this data, and the per-sweep / per-camera HTTP POSTs
    // were stalling navigation by hundreds of ms each on slow networks.
    if (!PROJECT_CONFIG.features.chatEnabled) return;
    try {
      // Log the full state for debugging
      console.log('🔍 Current state:', {
        sdk: !!this.state.sdk,
        isConnected: this.state.isConnected,
        currentSweep: this.state.currentSweep,
        currentTag: this.state.currentTag,
        spatialContext: this.state.spatialContext
      });

      // Validate spatial context before sending
      if (!this.state.spatialContext) {
        console.warn('⚠️ No spatial context available');
        return;
      }

      console.log('📤 Preparing to send spatial context to backend:', {
        sweep: this.state.spatialContext.current_sweep,
        position: this.state.spatialContext.current_position,
        tag: this.state.spatialContext.current_tag ? {
          label: this.state.spatialContext.current_tag.label,
          distance: this.state.spatialContext.current_tag.distance
        } : null
      });
      
      // Make the API call
      const response = await apiService.updateSpatialContext(this.state.spatialContext);
      console.log('✅ Backend response:', response);
      
      if (response.success) {
        console.log('✅ Backend spatial context updated successfully');
        console.log('📦 Current backend context:', response.current_context);
      } else {
        console.warn('⚠️ Backend update succeeded but returned success: false');
      }
    } catch (error) {
      console.error('❌ Backend spatial context update failed:', {
        message: error.message,
        stack: error.stack,
        state: {
          sweep: this.state.spatialContext?.current_sweep,
          position: this.state.spatialContext?.current_position,
          tag: this.state.spatialContext?.current_tag
        }
      });
    }
  }

  // Open a specific tag
  async openTag(tagName) {
    if (!this.state.sdk) {
      console.warn('⚠️ SDK not available for tag opening');
      return false;
    }

    try {
      console.log(`🏷️ Attempting to open tag: ${tagName}`);

      // Use cached tags to avoid deprecated Mattertag.getData() call
      const targetTag = this.state.tags.find((tag) =>
        tag.label && tag.label.toLowerCase().includes(tagName.toLowerCase())
      );

      if (!targetTag) {
        console.log(`❌ Tag not found: ${tagName}`);
        return false;
      }

      // Open the mattertag
      await this.state.sdk.Mattertag.navigateToTag(targetTag.id, this.state.sdk.Mattertag.Transition.FLY);

      // Wait a bit then try to open the tag content
      setTimeout(async () => {
        try {
          await this.state.sdk.Mattertag.openTag(targetTag.id);
          console.log(`✅ Successfully opened tag: ${tagName}`);
        } catch (e) {
          console.log(`⚠️ Could not open tag content for: ${tagName}`);
        }
      }, TIMING.MATTERPORT.TAG_OPEN_DELAY);

      return true;
    } catch (error) {
      console.error('❌ Failed to open tag:', error);
      return false;
    }
  }

  // Rotate camera to face a specific tag
  async rotateCameraToTag(tagName) {
    if (!this.state.sdk) {
      console.warn('⚠️ SDK not available for camera rotation');
      return false;
    }

    try {
      console.log(`📷 Rotating camera to face tag: ${tagName}`);

      // Use cached tags to avoid deprecated Mattertag.getData() call
      const targetTag = this.state.tags.find((tag) =>
        tag.label && tag.label.toLowerCase().includes(tagName.toLowerCase())
      );

      if (!targetTag) {
        console.log(`❌ Tag not found for camera rotation: ${tagName}`);
        return false;
      }

      // Get tag position
      const tagPosition = targetTag.position;
      if (!tagPosition) {
        console.log(`❌ Tag position not available: ${tagName}`);
        return false;
      }

      // Rotate camera to face the tag position
      return await this.rotateCameraToPosition(tagPosition.x, tagPosition.y, tagPosition.z);
    } catch (error) {
      console.error('❌ Failed to rotate camera to tag:', error);
      return false;
    }
  }

  // Rotate camera to face a specific position
  async rotateCameraToPosition(x, y, z) {
    if (!this.state.sdk) {
      console.warn('⚠️ SDK not available for camera rotation');
      return false;
    }

    try {
      console.log(`📷 Rotating camera to face position: (${x}, ${y}, ${z})`);

      // Get current camera position
      const currentPose = await this.state.sdk.Camera.pose.get();
      const currentPosition = currentPose.position;

      // Calculate direction vector from current position to target position
      const direction = {
        x: x - currentPosition.x,
        y: y - currentPosition.y,
        z: z - currentPosition.z
      };

      // Calculate rotation to face the target
      const rotation = {
        x: 0, // Pitch (looking up/down)
        y: Math.atan2(direction.x, direction.z), // Yaw (looking left/right)
        z: 0 // Roll (tilt)
      };

      // Set camera rotation
      await this.state.sdk.Camera.pose.set({
        position: currentPosition,
        rotation: rotation
      });

      console.log(`✅ Camera rotated to face position`);
      return true;
    } catch (error) {
      console.error('❌ Failed to rotate camera to position:', error);
      return false;
    }
  }

  // Navigation function
  async navigateToTag(label) {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot navigate: SDK not initialized');
      return false;
    }

    try {
      console.log('🧭 Attempting to navigate to:', label);
      console.log('🧭 Available tags:', this.state.tags.length);
      
      // If no tags are available, provide helpful feedback
      if (this.state.tags.length === 0) {
        console.log('🧭 No tags available for navigation');
        return false;
      }
      
      console.log('🧭 Available tag labels:', this.state.tags.map(t => t.label));
      
      // Try using the new Tag API first
      try {
        console.log('🏷️ Trying Tag.data.waitUntil API...');
        const tagData = await this.state.sdk.Tag.data.waitUntil((collection) =>
          Object.keys(collection).length > 0
        );

        const targetTag = Object.values(tagData).find((tag) =>
          tag.label?.toLowerCase().includes(label.toLowerCase()) ||
          tag.id === label
        );

        if (targetTag && targetTag.anchorPosition) {
          console.log('🏷️ Found tag with anchorPosition:', targetTag);
          const pos = targetTag.anchorPosition;
          const success = await this.navigateToPosition(pos.x, pos.y, pos.z);
          if (success) {
            // Update current tag context
            this.state.currentTag = targetTag;
            this.state.spatialContext.current_tag = targetTag;
            this.notifyEventHandlers('navigated', targetTag);
            console.log('✅ Navigation successful via Tag API');
            
            // Open the tag and rotate camera after successful navigation
            await this.openTag(label);
            await this.rotateCameraToTag(label);
            
            return true;
          }
        }
      } catch (tagError) {
        console.log('⚠️ Tag.data.waitUntil API failed, trying fallback:', tagError.message);
      }

      // Fallback to legacy Mattertag API
      try {
        console.log('🏷️ Trying Mattertag.getData API...');
        const tags = await this.state.sdk.Mattertag.getData();
        const targetTag = tags.find((tag) =>
          tag.label?.toLowerCase().includes(label.toLowerCase()) ||
          tag.sid === label
        );

        if (targetTag && targetTag.anchorPosition) {
          console.log('🏷️ Found tag with anchorPosition via Mattertag:', targetTag);
          const success = await this.navigateToPosition(
            targetTag.anchorPosition.x,
            targetTag.anchorPosition.y,
            targetTag.anchorPosition.z
          );
          if (success) {
            // Update current tag context
            this.state.currentTag = targetTag;
            this.state.spatialContext.current_tag = targetTag;
            this.notifyEventHandlers('navigated', targetTag);
            console.log('✅ Navigation successful via Mattertag API');
            
            // Open the tag and rotate camera after successful navigation
            await this.openTag(label);
            await this.rotateCameraToTag(label);
            
            return true;
          }
        }
      } catch (mattertagError) {
        console.warn('⚠️ Mattertag API failed:', mattertagError.message);
      }

      // Fallback to original method with our stored tags
      const targetTag = this.state.tags.find(tag => 
        tag.label && tag.label.toLowerCase().includes(label.toLowerCase())
      );

      if (targetTag) {
        console.log('🧭 Found target tag in stored tags:', targetTag);
        
        // Try different navigation methods
        if (targetTag.sweep && targetTag.sweep.trim() !== '') {
          console.log('🧭 Navigating via sweep:', targetTag.sweep);
          await this.state.sdk.Sweep.moveTo(targetTag.sweep);
        } else if (targetTag.position && (targetTag.position.x !== 0 || targetTag.position.y !== 0 || targetTag.position.z !== 0)) {
          console.log('🧭 Navigating via position:', targetTag.position);
          const success = await this.navigateToPosition(targetTag.position.x, targetTag.position.y, targetTag.position.z);
          if (!success) {
            console.warn('🧭 Position navigation failed');
            return false;
          }
        } else {
          console.warn('🧭 Tag found but no navigation method available - no sweep ID or position');
          
          // Try to use Mattertag navigation if available
          if (this.state.sdk.Mattertag && typeof this.state.sdk.Mattertag.navigateTo === 'function') {
            console.log('🧭 Trying Mattertag navigation...');
            await this.state.sdk.Mattertag.navigateTo(targetTag.id);
          } else if (this.state.sdk.Tag && typeof this.state.sdk.Tag.navigateTo === 'function') {
            console.log('🧭 Trying Tag navigation...');
            await this.state.sdk.Tag.navigateTo(targetTag.id);
          } else {
            console.warn('🧭 No navigation method available for this tag');
            return false;
          }
        }
        
        // Update current tag context
        this.state.currentTag = targetTag;
        this.state.spatialContext.current_tag = targetTag;
        
        this.notifyEventHandlers('navigated', targetTag);
        console.log('✅ Navigation successful');
        
        // Open the tag and rotate camera after successful navigation
        await this.openTag(label);
        await this.rotateCameraToTag(label);
        
        return true;
      } else {
        console.warn('❌ Tag not found:', label);
        console.warn('❌ Available tags:', this.state.tags.map(t => t.label));
        return false;
      }
    } catch (error) {
      console.error('❌ Navigation error:', error);
      return false;
    }
  }

  // Navigate to a specific 3D position
  async navigateToPosition(x, y, z) {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot navigate: SDK not initialized');
      return false;
    }

    try {
      console.log(`🧭 Navigating to position: (${x}, ${y}, ${z})`);
      
      // Get all available sweeps
      const sweepData = await this.state.sdk.Sweep.data.waitUntil((collection) =>
        Object.keys(collection).length > 0
      );

      // Find the closest sweep to the target position
      let closestSweep = null;
      let minDistance = Infinity;

      for (const sweepId of Object.keys(sweepData)) {
        const sweep = sweepData[sweepId];
        if (sweep && sweep.position) {
          const distance = Math.sqrt(
            Math.pow(sweep.position.x - x, 2) +
            Math.pow(sweep.position.y - y, 2) +
            Math.pow(sweep.position.z - z, 2)
          );
          if (distance < minDistance) {
            minDistance = distance;
            closestSweep = sweep;
          }
        }
      }

      if (closestSweep) {
        await this.state.sdk.Sweep.moveTo(closestSweep.id, {
          transition: this.state.sdk.Sweep.Transition.FLY,
          transitionTime: 1000
        });
        console.log(`✅ Navigated to closest sweep: ${closestSweep.id}`);
        return true;
      } else {
        console.warn('⚠️ No suitable sweep found for navigation');
        return false;
      }
    } catch (error) {
      console.error('❌ Position navigation error:', error);
      return false;
    }
  }

  // Get current spatial info
  getCurrentSpatialInfo() {
    return {
      current_sweep: this.state.currentSweep,
      current_tag: this.state.currentTag,
      spatial_context: this.state.spatialContext,
      tags: this.state.tags
    };
  }

  // Get available tags for navigation
  getAvailableTags() {
    return this.state.tags;
  }

  // Check if SDK is connected
  isConnected() {
    return this.state.isConnected;
  }

  // Get SDK instance
  getSDK() {
    return this.state.sdk;
  }

  // Event handling
  addEventListener(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    const handlers = this.eventHandlers.get(event);
    // Prevent duplicate registrations
    if (!handlers.includes(handler)) {
      handlers.push(handler);
    }
  }

  removeEventListener(event, handler) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  notifyEventHandlers(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // Handle tag events (click, hover, etc.)
  handleTagEvent(event, eventType) {
    try {
      console.log(`🏷️ Processing tag ${eventType} event:`, event);
      
      if (eventType === 'click' || eventType === 'hover') {
        const rawId = event.sid || event.id || event.tagId;

        // Look up the full tag from our stored tags (sid from Mattertag matches tag.id)
        const storedTag = this.state.tags.find(
          (t) => t.id === rawId || t.id === event.id || t.id === event.tagId
        );

        const tagData = {
          id: rawId,
          label: storedTag?.label || event.label || event.title || 'Unnamed',
          description: storedTag?.description || event.description || '',
          position: storedTag?.position || event.anchorPosition || event.position || { x: 0, y: 0, z: 0 },
          sweep: storedTag?.sweep || event.sweep || event.sweepId || ''
        };
        
        if (tagData.id) {
          this.state.currentTag = tagData;
          this.state.spatialContext.current_tag = tagData;
          this.updateBackendSpatialContext();
          this.notifyEventHandlers('tagSelected', tagData);
        }
      }
    } catch (error) {
      console.error('❌ Error handling tag event:', error);
    }
  }

  // Handle tag closed event
  handleTagClosed() {
    console.log('🏷️ Tag closed, clearing current tag');
    
    // Clear current tag context
    this.state.currentTag = null;
    this.state.spatialContext.current_tag = null;
    
    // Update backend
    this.updateBackendSpatialContext();
    this.notifyEventHandlers('tagClosed', null);
  }

  // Cleanup
  cleanup() {
    this.eventHandlers.clear();
    this.state = new MatterportState();
    audioService.stop();
  }

  async autoNavigateOnStart() {
    if (this.autoNavigationCompleted || !this.state.sdk) {
      console.log('🚫 Auto-navigation already completed or SDK not initialized, skipping...');
      return;
    }
    try {
      console.log('🎯 Auto-navigation starting...');
      this.autoNavigationCompleted = true;

      // Get camera configuration from project config
      const { camera } = MATTERPORT_CONFIG;

      // Helper: capture the natural Matterport default pose for this sweep so
      // resetToStart() can restore the exact starting view without relying on
      // Camera.setRotation (which fails when the view mode isn't Panorama View).
      const captureInitialPose = async () => {
        // Wait for Camera.pose subscription to settle after navigation
        await new Promise(r => setTimeout(r, 500));
        try {
          const pose = await this.state.sdk.Camera.pose.get();
          this._initialPose = pose;
          this._initialSweepId = this.state.currentSweep;
          console.log('📸 Initial pose captured for reset — sweep:', this._initialSweepId,
            'rotation:', pose?.rotation);
        } catch (_) {
          // Fallback: use the pose already stored by the Camera.pose subscription
          if (this.state.currentPose) {
            this._initialPose = this.state.currentPose;
            this._initialSweepId = this.state.currentSweep;
          }
        }
      };

      // Try configured sweep ID first
      if (camera?.initialSweepId) {
        try {
          console.log(`🎯 Trying configured sweep ID: ${camera.initialSweepId}`);
          const rot = camera.initialRotation;
          const moveOpts = {
            transition: this.state.sdk.Sweep.Transition.INSTANT,
            transitionTime: 0,
          };
          if (rot && typeof rot.pitch === 'number' && typeof rot.yaw === 'number') {
            moveOpts.rotation = { x: rot.pitch, y: rot.yaw };
          }
          await this.state.sdk.Sweep.moveTo(camera.initialSweepId, moveOpts);
          console.log(`✅ Navigated to configured sweep: ${camera.initialSweepId}`);
          setTimeout(() => captureInitialPose(), 100);
          return;
        } catch (configuredSweepError) {
          console.log('⚠️ Configured sweep failed, trying natural order');
        }
      }

      // Try to get the first sweep from natural order
      try {
        console.log('🔍 Getting first sweep from Matterport natural order...');
        const sweepData = await this.state.sdk.Sweep.data.waitUntil(
          (collection) => Object.keys(collection).length > 0
        );

        if (sweepData && typeof sweepData === 'object') {
          const sweepIds = Object.keys(sweepData);
          if (sweepIds.length > 0) {
            const firstSweepId = sweepIds[0];
            console.log(`🎯 Found first sweep in natural order: ${firstSweepId}`);

            try {
              await this.state.sdk.Sweep.moveTo(firstSweepId, {
                transition: this.state.sdk.Sweep.Transition.INSTANT,
                transitionTime: 0
              });
              console.log(`✅ Immediately navigated to first white circle: ${firstSweepId}`);
              setTimeout(() => captureInitialPose(), 100);
              return;
            } catch (firstSweepError) {
              console.log('⚠️ First sweep navigation failed, trying fallbacks');
            }
          }
        }
      } catch (firstSweepError) {
        console.log('⚠️ Could not get first sweep from natural order');
      }

      // Fallback: Try known sweep IDs
      for (const testId of FALLBACK_SWEEP_IDS) {
        try {
          await this.state.sdk.Sweep.moveTo(testId, {
            transition: this.state.sdk.Sweep.Transition.INSTANT,
            transitionTime: 0
          });
          console.log(`✅ Immediately navigated to known sweep: ${testId}`);
          setTimeout(() => captureInitialPose(), 100);
          return;
        } catch (testError) {
          console.log(`⚠️ Failed to navigate to ${testId}, trying next...`);
        }
      }

      console.log('❌ All navigation attempts failed');
    } catch (error) {
      console.error('❌ Auto-navigation failed:', error);
    }
  }

  // Reset to initial state — clears the guard flag so autoNavigateOnStart can run again
  async resetToStart() {
    // Restore the exact pose captured on first load — flies to the starting sweep
    // with the same camera orientation the user saw when they first entered.
    // Rotation is passed directly to Sweep.moveTo (no separate setRotation needed).
    if (this._initialSweepId && this._initialPose && this.state.sdk?.Sweep) {
      try {
        await this.state.sdk.Sweep.moveTo(this._initialSweepId, {
          rotation: {
            x: this._initialPose.rotation.x,
            y: this._initialPose.rotation.y,
          },
          transition: this.state.sdk.Sweep.Transition.FLY,
          transitionTime: 1000,
        });
        return;
      } catch (err) {
        console.warn('⚠️ Could not fly to initial pose, falling back to autoNavigateOnStart:', err?.message);
      }
    }
    // Fallback: full re-navigation (initial pose not captured yet)
    this.autoNavigationCompleted = false;
    await this.autoNavigateOnStart();
  }

  /**
   * Walk step-by-step from the current sweep to targetSweepId using BFS,
   * facing the direction of movement at each step.
   * Returns true if path was walked, false if fallback (direct jump) is needed.
   */
  async _walkPath(targetSweepId, stepTimeMs = 500, finalRotation = null) {
    const sdk = this.state.sdk;
    const fromIdRaw = this.state.currentSweep;

    if (!fromIdRaw) {
      console.warn('⚠️ _walkPath: currentSweep not yet set, falling back to direct jump');
      return false;
    }

    const sweeps = Array.from(this._sweepCache.values()).filter(s => s && s.position);
    if (sweeps.length < 2) {
      console.warn(`⚠️ _walkPath: sweep cache has only ${sweeps.length} sweeps, falling back`);
      return false;
    }

    // Build lookup by id and sid
    const byId  = new Map(sweeps.map(s => [s.id,  s]));
    const bySid = new Map(sweeps.filter(s => s.sid).map(s => [s.sid, s]));
    const resolve = key => byId.get(key) || bySid.get(key);

    const fromSweep   = resolve(fromIdRaw);
    const targetSweep = resolve(targetSweepId);

    if (!fromSweep) {
      console.warn(`⚠️ _walkPath: from "${fromIdRaw}" not in cache. Cache sample:`, sweeps.slice(0, 2).map(s => `id=${s.id} sid=${s.sid}`));
      return false;
    }
    if (!targetSweep) {
      console.warn(`⚠️ _walkPath: target "${targetSweepId}" not in cache (tried id+sid). Cache sample:`, sweeps.slice(0, 2).map(s => `id=${s.id} sid=${s.sid}`));
      return false;
    }

    const fromId = fromSweep.id;
    const toId   = targetSweep.id;
    if (fromId === toId) return true;

    // Try SDK A* on the official sweep graph first — this is Matterport's
    // own walkable connectivity, so the result is the true shortest path.
    let path = null;
    try {
      if (sdk.Sweep?.createGraph && sdk.Graph?.createAStarRunner && sdk.Graph?.AStarStatus) {
        const sweepGraph = this._sweepGraph || (this._sweepGraph = await sdk.Sweep.createGraph());
        const startV = sweepGraph.vertex(fromId);
        const endV   = sweepGraph.vertex(toId);
        if (startV && endV) {
          const heuristic = (a, b) => {
            const ap = a.data?.position, bp = b.data?.position;
            if (!ap || !bp) return 0;
            const dx = ap.x - bp.x, dy = ap.y - bp.y, dz = ap.z - bp.z;
            return Math.sqrt(dx*dx + dy*dy + dz*dz);
          };
          const result = sdk.Graph.createAStarRunner(sweepGraph, startV, endV, { heuristic }).exec();
          if (result?.status === sdk.Graph.AStarStatus.SUCCESS && result.path?.length) {
            // path[0] is the start vertex — drop it so we only emit hops to take.
            path = result.path.slice(1).map(v => v.id);
            console.log(`🚶 SDK A*: ${path.length} steps → "${toId}"`);
          }
        }
      }
    } catch (e) {
      console.warn('⚠️ SDK A* failed, falling back to Dijkstra:', e?.message);
    }

    // Dijkstra fallback on a Euclidean-distance graph: edge weight = real
    // walking distance, so the result is the shortest physical path
    // (BFS minimises hop count and can route through detours).
    if (!path) {
      const THRESHOLDS = [8, 12, 16, 22, 30];
      for (const EDGE_DIST of THRESHOLDS) {
        const adj = new Map(sweeps.map(s => [s.id, []]));
        for (let i = 0; i < sweeps.length; i++) {
          for (let j = i + 1; j < sweeps.length; j++) {
            const a = sweeps[i], b = sweeps[j];
            const dx = a.position.x - b.position.x;
            const dy = a.position.y - b.position.y;
            const dz = a.position.z - b.position.z;
            const d = Math.sqrt(dx*dx + dy*dy + dz*dz);
            if (d <= EDGE_DIST) {
              adj.get(a.id).push({ id: b.id, w: d });
              adj.get(b.id).push({ id: a.id, w: d });
            }
          }
        }

        const dist = new Map(sweeps.map(s => [s.id, Infinity]));
        const parent = new Map();
        dist.set(fromId, 0);
        const remaining = new Set(sweeps.map(s => s.id));
        while (remaining.size) {
          let u = null, best = Infinity;
          for (const id of remaining) {
            const d = dist.get(id);
            if (d < best) { best = d; u = id; }
          }
          if (u === null || best === Infinity) break;
          remaining.delete(u);
          if (u === toId) break;
          for (const { id: v, w } of (adj.get(u) || [])) {
            if (!remaining.has(v)) continue;
            const alt = best + w;
            if (alt < dist.get(v)) { dist.set(v, alt); parent.set(v, u); }
          }
        }

        if (dist.get(toId) !== Infinity) {
          path = [];
          let cur = toId;
          while (cur !== fromId) { path.unshift(cur); cur = parent.get(cur); }
          console.log(`🚶 Dijkstra: ${path.length} steps (maxStepDist=${EDGE_DIST}m) → "${toId}"`);
          break;
        }
      }
    }

    if (!path) {
      console.warn(`⚠️ No path from "${fromId}" → "${toId}". Falling back.`);
      return false;
    }
    window.dispatchEvent(new CustomEvent('navigationStart'));

    // Walk smoothly: take every 2nd sweep (small hops along the path) and
    // make each hop short. Larger stride causes visible jumps; stride=2
    // keeps hops ~1–3 m apart, which reads as continuous walking. The path
    // always ends exactly on the destination sweep at the configured angle.
    const stride = 2;
    const stridedPath = path.filter((_, i) => (i + 1) % stride === 0 || i === path.length - 1);

    // Bound total walk time so reaching the next stop takes ~1.5 s
    // regardless of path length. `stepTimeMs` is the per-hop budget when
    // the path is short; on long paths we shrink each hop so the whole
    // walk still finishes inside the target window.
    const TARGET_TOTAL_MS = 2500;
    const MIN_HOP_MS = 60;
    const hopMs = Math.max(MIN_HOP_MS, Math.min(stepTimeMs, Math.round(TARGET_TOTAL_MS / Math.max(1, stridedPath.length))));

    let prevId = fromId;
    for (let i = 0; i < stridedPath.length; i++) {
      const sid = stridedPath[i];
      const isLastHop = i === stridedPath.length - 1;
      const prev = byId.get(prevId);
      const next = byId.get(sid);
      const moveOpts = { transition: sdk.Sweep.Transition.FLY, transitionTime: hopMs };
      if (isLastHop && finalRotation && (finalRotation.x != null || finalRotation.y != null)) {
        // Final hop lands at the configured camera angle for the stop
        // so the user faces the intended view, not the walking heading.
        moveOpts.rotation = { x: finalRotation.x ?? 0, y: finalRotation.y ?? 0 };
      } else if (prev?.position && next?.position) {
        const dx = next.position.x - prev.position.x;
        const dz = next.position.z - prev.position.z;
        moveOpts.rotation = { x: 0, y: Math.atan2(-dx, -dz) * (180 / Math.PI) };
      }
      await sdk.Sweep.moveTo(sid, moveOpts);
      this.state.currentSweep = sid;
      prevId = sid;
    }

    window.dispatchEvent(new CustomEvent('navigationEnd'));
    return true;
  }

  // Navigate to a specific sweep with rotation
  async navigateToSweepWithRotation(sweepId, rotation) {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot navigate: SDK not initialized');
      return false;
    }

    try {
      console.log(`🧭 Navigating to sweep ${sweepId} with rotation:`, rotation);

      // Walk the floor path; fall back to direct FLY if no path found
      await this._walkPath(sweepId, 500);
      await this.state.sdk.Camera.setRotation({
        x: rotation.pitch,
        y: rotation.yaw,
        z: rotation.roll
      });

      console.log('✅ Navigation and rotation completed');
      return true;
    } catch (error) {
      console.error('❌ Navigation with rotation failed:', error);
      return false;
    }
  }

  // Navigate to a specific sweep
  async navigateToSweep(sweepId, transitionTime = 1000) {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot navigate: SDK not initialized');
      return false;
    }

    try {
      console.log(`🧭 Navigating to sweep ${sweepId} with transition time ${transitionTime}`);
      if (transitionTime === 0) {
        await this.state.sdk.Sweep.moveTo(sweepId, {
          transition: this.state.sdk.Sweep.Transition.INSTANT,
          transitionTime: 0
        });
      } else {
        // Walk the floor path; fall back to direct FLY if no path found
        const walked = await this._walkPath(sweepId, 500);
        if (!walked) {
          await this.state.sdk.Sweep.moveTo(sweepId, {
            transition: this.state.sdk.Sweep.Transition.FLY,
            transitionTime
          });
        }
      }
      console.log('✅ Navigation completed');
      return true;
    } catch (error) {
      console.error('❌ Navigation failed:', error);
      return false;
    }
  }

  // Navigate to a sweep with instant rotation
  async navigateToSweepWithInstantRotation(sweepId, rotation) {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot navigate: SDK not initialized');
      return false;
    }

    try {
      console.log(`🧭 Navigating to sweep ${sweepId} with instant rotation:`, rotation);
      
      // First navigate to the sweep instantly
      await this.state.sdk.Sweep.moveTo(sweepId, {
        transition: this.state.sdk.Sweep.Transition.INSTANT,
        transitionTime: 0
      });

      // Wait a tiny bit for the sweep change to complete
      await new Promise(resolve => setTimeout(resolve, 50));

      // Then apply rotation directly using Camera.setRotation
      await this.state.sdk.Camera.setRotation({
        x: rotation.x || rotation.pitch || 0,
        y: rotation.y || rotation.yaw || 0,
        z: rotation.z || rotation.roll || 0
      });

      console.log('✅ Instant navigation and rotation completed');
      return true;
    } catch (error) {
      console.error('❌ Instant navigation with rotation failed:', error);
      return false;
    }
  }

  // Get all available sweep IDs
  async getAllSweepIds() {
    if (!this.state.sdk) {
      console.warn('⚠️ Cannot get sweeps: SDK not initialized');
      return [];
    }

    try {
      console.log('🔍 Getting all sweep IDs...');
      const sweepData = await this.state.sdk.Sweep.data.waitUntil(
        (collection) => Object.keys(collection).length > 0
      );

      const sweepIds = Object.keys(sweepData);
      console.log('📋 Available sweep IDs:', sweepIds);
      return sweepIds;
    } catch (error) {
      console.error('❌ Failed to get sweep IDs:', error);
      return [];
    }
  }

  // Check if a sweep ID is a 360 view
  is360View(sweepId) {
    const view360SweepIds = VIEW_360_CONFIG.views.map(view => view.sweepId);
    return view360SweepIds.includes(sweepId);
  }

  // Get the last camera position
  getLastCameraPosition() {
    console.log('📷 Getting last camera position:', {
      hasPosition: !!this.state.lastCameraPosition,
      rotation: this.state.lastCameraPosition?.rotation,
      position: this.state.lastCameraPosition?.position
    });
    return this.state.lastCameraPosition;
  }

  // Get current 360 view state
  isIn360View() {
    console.log('🔍 Checking 360 view state:', this.state.is360View);
    return this.state.is360View;
  }

  // Cleanup all SDK subscriptions
  cleanupSubscriptions() {
    console.log(`🧹 Cleaning up ${this.subscriptions.length} SDK subscriptions...`);
    this.subscriptions.forEach((sub, index) => {
      try {
        if (sub && typeof sub.cancel === 'function') {
          sub.cancel();
        } else if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      } catch (err) {
        console.warn(`⚠️ Failed to cleanup subscription ${index}:`, err?.message);
      }
    });
    this.subscriptions = [];
    this.handlersSetup = false;
    console.log('✅ SDK subscriptions cleanup complete');
  }

  /**
   * Get all sweep data from the cache for floor plan mapping.
   * Returns an array of { id, sid, position, neighbors } objects.
   */
  getAllSweeps() {
    const sweeps = Array.from(this._sweepCache?.values() || [])
      .filter(s => s && s.position)
      .map(s => ({
        id: s.id,
        sid: s.sid,
        position: {
          x: s.position.x,
          y: s.position.y,
          z: s.position.z
        },
        neighbors: s.neighbors || []
      }));
    return sweeps;
  }

  /**
   * Log all sweep IDs with their positions to console for mapping to floor plan.
   * Call this from console: matterportService.logAllSweeps()
   */
  logAllSweeps() {
    const sweeps = this.getAllSweeps();
    console.log(`\n🗺️ ===== ALL SWEEP POSITIONS (${sweeps.length} sweeps) =====`);
    console.table(sweeps.map((s, i) => ({
      '#': i + 1,
      id: s.id,
      sid: s.sid || '(none)',
      x: s.position.x?.toFixed(2),
      y: s.position.y?.toFixed(2),
      z: s.position.z?.toFixed(2)
    })));
    
    // Also log as JSON for easy copy-paste into mapping config
    console.log('\n📋 JSON format for floor plan mapping:');
    console.log(JSON.stringify(sweeps.map(s => ({
      id: s.id,
      x: parseFloat(s.position.x?.toFixed(2)),
      z: parseFloat(s.position.z?.toFixed(2))
    })), null, 2));
    
    console.log('🗺️ ==========================================\n');
    return sweeps;
  }

  /**
   * Get current position for floor plan display.
   * Returns { sweepId, position: { x, y, z } } or null.
   */
  getCurrentPosition() {
    const sweepId = this.state.currentSweep;
    const position = this.state.spatialContext?.current_position || 
                     this.state.currentPose?.position;
    
    if (!position) return null;
    
    return {
      sweepId,
      position: {
        x: position.x,
        y: position.y,
        z: position.z
      }
    };
  }

  /**
   * Subscribe to position changes for floor plan updates.
   * Callback receives { sweepId, position } on each camera/sweep change.
   */
  onPositionChange(callback) {
    this.addEventListener('cameraMoved', (pose) => {
      if (pose?.position) {
        callback({
          sweepId: this.state.currentSweep,
          position: pose.position
        });
      }
    });
    
    this.addEventListener('sweepChanged', (sweepData) => {
      if (sweepData?.position) {
        callback({
          sweepId: sweepData.id,
          position: sweepData.position
        });
      }
    });
  }
}

// Create singleton instance
const matterportService = new MatterportService();
export default matterportService; 